// src/hooks/useMontages.ts — SCE Issues
// Les montages sous-traités (29/10), lus une fois par session et partagés par
// la page « Montage sous-traité », la fiche équipement et le menu du compte.
// Après une attribution, `publierMontages` les diffuse aux composants montés.
//
// Droit d'attribuer : tout coordinateur ou suppléant, quel que soit son métier,
// et l'administrateur (Guillaume, 29/10) — sur l'identité AFFICHÉE, comme les
// couleurs des métiers ; en bascule de rôle, l'écriture demande confirmation.

import { useEffect, useState } from 'react'
import { lireMontagesEnregistres } from '@/api/montages.api'
import { isCoordinateur } from '@/config/coordinateurs.config'
import { estAdmin } from '@/config/admin.config'
import { MONTAGES_VIDES, type Montages } from '@/utils/montages'

let promesse: Promise<Montages | null> | null = null
const abonnes = new Set<(m: Montages) => void>()

function charger(): Promise<Montages | null> {
  // Un échec de lecture ne casse aucune page : pas de badge, c'est tout.
  promesse ??= lireMontagesEnregistres().catch(() => null)
  return promesse
}

/** Après une écriture : les nouveaux montages, tout de suite, partout. */
export function publierMontages(m: Montages): void {
  promesse = Promise.resolve(m)
  for (const f of abonnes) f(m)
}

export function peutAttribuerMontages(upn: string | undefined): boolean {
  return !!upn && (isCoordinateur(upn) || estAdmin(upn))
}

export interface EtatMontages {
  montages: Montages
  chargement: boolean
  /** La lecture a échoué : ne rien affirmer (pas de « à attribuer »). */
  erreur: boolean
}

export function useMontages(): EtatMontages {
  const [montages, setMontages] = useState<Montages>(MONTAGES_VIDES)
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState(false)

  useEffect(() => {
    let actif = true
    void charger().then((m) => {
      if (!actif) return
      setMontages(m ?? MONTAGES_VIDES)
      setErreur(m === null)
      setChargement(false)
    })
    const abonne = (m: Montages): void => {
      setMontages(m)
      setErreur(false)
    }
    abonnes.add(abonne)
    return () => {
      actif = false
      abonnes.delete(abonne)
    }
  }, [])

  return { montages, chargement, erreur }
}
